import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@/store';
import { Eye, EyeOff } from 'lucide-react';
import { login } from '@/store/thunks/authThunks';
import type { RootState } from '@/store';
import { BASE_URL } from '@/config';
import Logo from '@/components/Logo';
import { Input, Button } from '@/components/ui';

const schema = Yup.object({
  login: Yup.string().required('Login requis'),
  password: Yup.string().required('Mot de passe requis'),
});

export default function Login() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector((s: RootState) => s.auth.isAuthenticated);
  const [showPassword, setShowPassword] = useState(false);

  const redirectTo = (location.state as { from?: { pathname?: string } })?.from?.pathname ?? BASE_URL;

  const formik = useFormik({
    initialValues: { login: '', password: '' },
    validationSchema: schema,
    onSubmit: async (values) => {
      const res = await dispatch(login(values));
      if (login.fulfilled.match(res) && res.payload) {
        navigate(redirectTo, { replace: true });
      }
    },
  });

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-8">
      <div className="w-full max-w-[420px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
        {/* Card header */}
        <div className="border-b border-slate-100 bg-slate-50/80 px-8 pt-10 pb-8">
          <div className="flex justify-center">
            <Logo className="text-2xl" />
          </div>
          <p className="mt-4 text-center text-sm text-slate-500">
            Connectez-vous à votre compte
          </p>
        </div>

        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="space-y-5 px-8 py-8">
          <Input
            id="login"
            label="Login"
            type="text"
            autoComplete="username"
            placeholder="Votre identifiant"
            error={formik.touched.login ? formik.errors.login : undefined}
            {...formik.getFieldProps('login')}
          />

          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-slate-700">
              Mot de passe
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Votre mot de passe"
                className="pro-input w-full pr-11"
                {...formik.getFieldProps('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                tabIndex={-1}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <p className="mt-1.5 text-sm text-red-600">{formik.errors.password}</p>
            )}
          </div>

          <Button
            type="submit"
            fullWidth
            size="lg"
            loading={formik.isSubmitting}
            className="mt-2"
          >
            {formik.isSubmitting ? 'Connexion...' : 'Connexion'}
          </Button>
        </form>
      </div>
    </div>
  );
}
